import { useCallback, useMemo, useState } from 'react'

import { randomIntFromInterval } from '~/bootcamp/Activities/utils/activities.utils'

const LEVEL_DENOMINATOR_RANGE_MAP: { [key: number]: { min: number; max: number } } = {
  1: { min: 2, max: 5 },
  2: { min: 4, max: 13 },
}

interface UseImproperFractionReturnType {
  numerator?: number
  denominator: number
  formValid: boolean
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
}

interface UseImproperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function useImproperFraction({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseImproperFractionProps): UseImproperFractionReturnType {
  const { max: maxDenominator, min: minDenominator } = LEVEL_DENOMINATOR_RANGE_MAP[difficultyLevel]
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [numerator, setNumerator] = useState<number>()
  const denominator = useMemo(
    () => randomIntFromInterval(minDenominator, maxDenominator, taskHistory),
    [minDenominator, maxDenominator, taskHistory],
  )

  const handleCheckAnswer = useCallback((): void => {
    if (numerator === null || numerator === undefined) return
    onAnswerStateChange(numerator >= denominator)
  }, [numerator, denominator, onAnswerStateChange])

  const onInputChange = useCallback((val?: string) => setNumerator(val || val === '0' ? +val : undefined), [])

  const goToNextQuestion = useCallback(() => {
    const newTaskHistory = [denominator, ...taskHistory].slice(0, maxDenominator - minDenominator)
    setTaskHistory(newTaskHistory)
    onInputChange()
    onNextQuestionClick()
  }, [minDenominator, maxDenominator, taskHistory, setTaskHistory, denominator, onInputChange, onNextQuestionClick])

  return {
    numerator,
    denominator,
    formValid: numerator !== undefined,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  } as const
}

export default useImproperFraction
